<template>
  <!-- Системная строка — по центру между линиями: по ней видно, на что человек
       реагировал, и она не должна выглядеть репликой. -->
  <div v-if="isSystem" class="msg-system">
    <span class="msg-system__line" />
    <span class="msg-system__text">
      {{ text }}
      <a v-if="link" class="msg-system__link" :href="link.url">{{ link.label || 'Открыть' }}</a>
    </span>
    <span class="msg-system__line" />
  </div>

  <div v-else class="msg-row" :class="{ 'msg-row--mine': mine }">
    <div class="msg-row__inner">
      <!-- Имя автора — только у чужих: своё имя человек знает. -->
      <div v-if="!mine && authorName" class="msg-row__author">
        {{ authorName }}<span v-if="authorRole"> · {{ authorRole.toLowerCase() }}</span>
      </div>

      <div class="msg-bubble" :class="{ 'msg-bubble--mine': mine, 'msg-bubble--only-media': onlyImages }">
        <!-- Цитата. Недоступную рисуем плашкой, а не прячем: ответ повис бы
             в пустоте, и было бы непонятно, на что он отвечает. -->
        <div v-if="hasQuote" class="msg-quote" :class="{ 'msg-quote--gone': quoteBlank }">
          <span class="msg-quote__author">{{ reply?.actor_name || 'Сообщение' }}</span>
          <span class="msg-quote__text">{{ quoteText }}</span>
        </div>

        <p v-if="text" class="msg-bubble__text">{{ text }}</p>

        <!-- Пузырь без содержимого — это либо удалённое сообщение, либо тип,
             которого эта версия ещё не умеет. Пустой прямоугольник выглядит
             поломкой; лучше сказать, что там было. -->
        <p v-else-if="!hasFiles" class="msg-bubble__text msg-bubble__text--faded">{{ placeholder }}</p>

        <div v-if="hasFiles" class="msg-bubble__files">
          <template v-for="file in files" :key="file.attachment_uuid">
            <MessageAttachment :file="file" :mine="mine" />
            <!-- Файл приняли в материалы — клиенту это главное: значит
                 присылать заново не нужно. -->
            <span v-if="file.filed_as_document" class="msg-bubble__filed">
              принято: {{ file.filed_as_document.type_label || 'документ' }}
            </span>
          </template>
        </div>
      </div>

      <div class="msg-meta" :class="{ 'msg-meta--mine': mine }">
        <span class="msg-meta__time">{{ time }}</span>

        <!-- Галочки только под своим: «прочитал ли я сам» никому не интересно. -->
        <span v-if="mine" class="msg-meta__read" :class="{ 'is-read': isRead }">
          <svg v-if="isRead" width="16" height="10" viewBox="0 0 20 12" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 6.5 4.5 10 12 2" /><path d="M8 8.5 9.5 10 17 2" />
          </svg>
          <svg v-else width="12" height="10" viewBox="0 0 14 12" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M1 6.5 4.5 10 12 2" />
          </svg>
        </span>

        <!-- Кто занёс чужие слова. Без этой подписи человек видит в своём чате
             реплику, которой не отправлял. -->
        <span v-if="recordedBy" class="msg-meta__recorded">записал(а) {{ recordedBy }}</span>

        <span v-if="channelLabel" class="msg-meta__channel">{{ channelLabel }}</span>

        <!-- «Ответить» стоит В СТРОКЕ МЕТЫ, а не в пузыре. Внутри своего,
             синего, пузыря кнопка читалась бы белым по синему, а у пузыря из
             одних картинок фон вовсе прозрачный (--only-media) — там уже
             ловили белое по белому. -->
        <button
          v-if="canReply"
          type="button"
          class="msg-meta__reply"
          @click="$emit('reply', message)"
        >Ответить</button>
      </div>
    </div>
  </div>
</template>

<script>
import MessageAttachment from './MessageAttachment.vue';

/** Каналы, о которых стоит сказать. «Кабинет» — это здесь, о нём молчим. */
const CHANNELS = {
  phone: 'Телефонный разговор',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  email: 'Почта',
};

export default {
  name: 'MessageBubble',
  components: { MessageAttachment },
  props: {
    message: { type: Object, required: true },
    /**
     * Можно ли отвечать. Архивный чат читается, но не пишется: композитора там
     * нет вовсе, и кнопка «Ответить» вела бы в никуда — цитата выставилась бы,
     * а показать её и отправить нечем.
     */
    replyable: { type: Boolean, default: true },
  },
  emits: ['reply'],
  computed: {
    meta() {
      return this.message.meta || {};
    },
    data() {
      return this.message.data || {};
    },
    isSystem() {
      return ['system_event', 'participant_event'].includes(this.message.type);
    },
    /**
     * Своё ли сообщение.
     *
     * Берём `is_mine` от сервера, а не сравниваем номера сами: номера клиентов
     * и сотрудников пересекаются, и «42» — это два разных человека.
     */
    mine() {
      return !!this.meta.is_mine;
    },
    text() {
      return this.data.text || '';
    },
    files() {
      return this.data.attachments || [];
    },
    hasFiles() {
      return this.files.length > 0;
    },
    /**
     * Пузырь из одних КАРТИНОК — тот, у которого рамку можно убрать.
     *
     * Проверка именно на `render`, а не на «текста нет, файлы есть». Своё
     * сообщение из одного PDF попадало под второе условие: прозрачный фон
     * побеждал синий (объявлен ниже при равной силе), а белый цвет текста от
     * `--mine` оставался — имя файла, размер и иконка белым по белой ленте.
     * Клиент видел на месте отправленного диплома пустое место и слал его
     * второй и третий раз.
     */
    onlyImages() {
      return !this.text
        && this.hasFiles
        && this.files.every((file) => file.render === 'image');
    },
    authorName() {
      return this.meta.actor?.name || '';
    },
    authorRole() {
      return this.meta.actor?.role_label || '';
    },
    reply() {
      return this.meta.reply_to_preview || null;
    },
    /**
     * Цитата ЕСТЬ, даже когда превью не приехало.
     *
     * Так выглядит цитата на сообщение из другого разговора и на давно
     * удалённое: номер заполнен, превью пусто. Рисовали по одному превью — и
     * блок пропадал совсем, а ответ повисал в пустоте. Контракт этого прямо
     * не разрешает: «не прячьте блок цитаты совсем».
     */
    hasQuote() {
      return !!(this.reply || this.meta.reply_to_message_uuid);
    },
    /** Показать нечего — рисуем приглушённо, но рисуем. */
    quoteBlank() {
      return !this.reply || !this.reply.text
        || ['unavailable', 'deleted'].includes(this.reply.render_hint);
    },
    quoteText() {
      if (!this.reply) return 'сообщение недоступно';
      if (this.reply.render_hint === 'unavailable') return 'сообщение недоступно';
      if (this.reply.render_hint === 'deleted') return 'сообщение удалено';
      // ПУСТОЙ ТЕКСТ — САМЫЙ ЧАСТЫЙ СЛУЧАЙ: сканы присылают молча, и текста у
      // такого сообщения нет вовсе. Без подписи цитата выглядела бы как
      // фамилия и пустая полоска.
      return this.reply.text || 'вложение';
    },
    /**
     * Есть ли на что отвечать.
     *
     * Системную строку не цитируют — это не реплика. Удалённое и незнакомый
     * тип тоже: у них нет ни текста, ни файлов, и цитата на них читалась бы
     * как поломка.
     */
    canReply() {
      return this.replyable
        && !this.isSystem
        && this.message.type !== 'deleted_placeholder'
        && (!!this.text || this.hasFiles);
    },
    /**
     * «Прочитано» — про СОБЕСЕДНИКА, а не «кто-то из участников».
     *
     * `by_client` для клиента означает его самого, поэтому в кабинете смотрим
     * на общий счёт прочитавших: сообщение клиента читают сотрудники.
     * `null` значит «вопрос неприменим» — галочки тогда нет вовсе.
     */
    isRead() {
      const read = this.meta.read || {};
      return (read.by_count || 0) > 0;
    },
    recordedBy() {
      return this.meta.recorded_by?.name || '';
    },
    channelLabel() {
      return CHANNELS[this.meta.channel] || '';
    },
    /**
     * Что писать вместо содержимого, когда его нет.
     *
     * Удалённое приходит своим типом с пустым содержимым — сервер не отдаёт
     * текст, а не «фронт прячет». Незнакомый тип появится раньше, чем сюда
     * доедет его отрисовка: падать или показывать пустоту одинаково плохо.
     */
    placeholder() {
      if (this.message.type === 'deleted_placeholder') return 'Сообщение удалено';
      return 'Это сообщение пока не поддерживается — откройте приложение';
    },
    /** Ссылка из системного события: «посмотреть план», «оплатить». */
    link() {
      const link = this.data.link;
      return link && link.url ? link : null;
    },
    time() {
      if (!this.message.created_at) return '';
      const d = new Date(this.message.created_at);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.msg-system {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 10px 0;

  &__line {
    flex: 1;
    height: 1px;
    background: #e8eaed;
  }

  &__text {
    flex: none;
    max-width: 70%;
    font-size: 12px;
    color: #6b7280;
    text-align: center;
  }

  &__link {
    margin-left: 6px;
    color: #11415d;
    font-weight: 700;
    text-decoration: underline;
  }
}

.msg-row {
  display: flex;
  margin-bottom: 10px;

  &--mine {
    justify-content: flex-end;
  }

  &__inner {
    max-width: 76%;
    min-width: 0;
  }

  &__author {
    margin-bottom: 4px;
    font-size: 11.5px;
    font-weight: 600;
    color: #6b7280;
  }
}

.msg-bubble {
  padding: 10px 13px;
  border-radius: 14px 14px 14px 4px;
  background: #f3f5f7;
  color: #111827;

  &--mine {
    border-radius: 14px 14px 4px 14px;
    background: #11415d;
    color: #fff;
  }

  // Пузырь из одной картинки не нужен: рамка вокруг фото — лишний шум.
  // Цвет возвращаем к обычному: фон убран, и белый текст `--mine` остался бы
  // белым на белой ленте — под картинкой так пропадала плашка «принято».
  &--only-media {
    padding: 4px;
    background: transparent;
    color: #111827;
  }

  &__text {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
    white-space: pre-line;
    word-break: break-word;
  }

  &__files {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__filed {
    align-self: flex-start;
    margin-top: 2px;
    padding: 2px 8px;
    border-radius: 999px;
    background: rgba(20, 212, 72, 0.14);
    color: #0f9c36;
    font-size: 11px;
    font-weight: 700;
  }

  &--mine &__filed {
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
  }

  // …но не в прозрачном пузыре: там за плашкой белая лента.
  &--mine.msg-bubble--only-media &__filed {
    background: rgba(20, 212, 72, 0.14);
    color: #0f9c36;
  }

  &__text--faded {
    opacity: 0.65;
    font-style: italic;
  }
}

.msg-quote {
  margin-bottom: 6px;
  padding: 5px 9px;
  border-left: 3px solid currentColor;
  border-radius: 0 8px 8px 0;
  background: rgba(17, 65, 93, 0.07);
  opacity: 0.9;

  /*
   * Светлая подложка — только там, где пузырь СИНИЙ. У пузыря из одних
   * картинок фон прозрачный, а текст тёмный (--only-media): белая подложка на
   * светлой ленте исчезала, и цитата оставалась строкой без рамки.
   */
  .msg-bubble--mine:not(.msg-bubble--only-media) & {
    background: rgba(255, 255, 255, 0.14);
  }

  &--gone {
    font-style: italic;
  }

  &__author {
    display: block;
    font-size: 11.5px;
    font-weight: 700;
  }

  &__text {
    display: block;
    font-size: 12.5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.msg-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
  margin-top: 4px;
  font-size: 11.5px;
  color: #9ca3af;

  &--mine {
    justify-content: flex-end;
  }

  &__read {
    display: flex;
    color: #9ca3af;

    &.is-read {
      color: #14d448;
    }
  }

  &__recorded,
  &__channel {
    color: #6b7280;
  }

  &__channel {
    padding: 1px 7px;
    border-radius: 999px;
    background: #f3f5f7;
  }

  /*
   * Кнопка ВИДНА ВСЕГДА, и прятать её по наведению нельзя.
   *
   * `opacity: 0` не освобождает место: невидимая кнопка продолжала занимать
   * свою ширину в строке меты, а у своих сообщений строка прижата вправо — и
   * время отъезжало от края ленты на ширину кнопки, которой не видно. Плюс на
   * касании hover не наступает никогда, а фокус на прозрачном элементе — это
   * фокус в пустоте.
   *
   * Шум от «Ответить» под каждым пузырём снимается цветом и размером, а не
   * исчезновением.
   */
  &__reply {
    padding: 0;
    border: 0;
    background: none;
    font: inherit;
    color: #6b7280;
    text-decoration: underline;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      color: #11415d;
    }
  }

}

@media (max-width: 768px) {
  .msg-row__inner {
    max-width: 86%;
  }
}
</style>
